/**
 * TradeImageCapturePanel — Click-to-paste capture zone for trade image extraction (KAN-82)
 *
 * States: ready → processing → completed | error
 * Reads clipboard image on click, sends to backend, returns extracted trade data.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TradeImageCaptureResult, MultiTradeRedirectResult } from '../../types/tradeImageCapture';
import { tradeImageCaptureService } from '../../services/api/tradeImageCaptureService';
import '../../styles/tradeImageCapture.css';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

interface TradeImageCapturePanelProps {
  clientId: string;
  onExtractionComplete: (result: TradeImageCaptureResult) => void;
  onMultiTradeDetected?: (                            // KAN-125
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number,
    sourceTypeName: string
  ) => void;
  onExtractionStart: () => void;
  onExtractionEnd: () => void;
  onClear: () => void;
  disabled: boolean;
  resetTrigger?: number; // increment to reset from parent
}

type CaptureState = 'ready' | 'processing' | 'completed' | 'error';

const TradeImageCapturePanel: React.FC<TradeImageCapturePanelProps> = ({
  clientId,
  onExtractionComplete,
  onMultiTradeDetected,
  onExtractionStart,
  onExtractionEnd,
  onClear,
  disabled,
  resetTrigger = 0,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<CaptureState>('ready');
  const [result, setResult] = useState<TradeImageCaptureResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const zoneRef = useRef<HTMLDivElement>(null);

  // Reset when parent increments resetTrigger
  const prevResetTrigger = useRef(resetTrigger);
  useEffect(() => {
    if (resetTrigger !== prevResetTrigger.current) {
      prevResetTrigger.current = resetTrigger;
      setState('ready');
      setResult(null);
      setErrorMessage(null);
      setImageDataUrl(null);
    }
  }, [resetTrigger]);

  const handleClick = async () => {
    if (state === 'processing' || disabled) return;
    if (state === 'completed') return; // Must click Clear first

    // Check clipboard API availability
    if (!navigator.clipboard?.read) {
      setErrorMessage(t('tradeBooking.imageCapture.clipboardUnavailable'));
      setState('error');
      setTimeout(() => { setState('ready'); setErrorMessage(null); }, 4000);
      return;
    }

    try {
      const clipboardItems = await navigator.clipboard.read();

      let imageBlob: Blob | null = null;
      let mimeType = '';

      for (const item of clipboardItems) {
        const imageType = item.types.find(type => ALLOWED_MIME_TYPES.includes(type));
        if (imageType) {
          imageBlob = await item.getType(imageType);
          mimeType = imageType;
          break;
        }
      }

      if (!imageBlob) {
        setErrorMessage(t('tradeBooking.imageCapture.noImageInClipboard'));
        setState('error');
        setTimeout(() => { setState('ready'); setErrorMessage(null); }, 4000);
        return;
      }

      // Check file size
      if (imageBlob.size > MAX_IMAGE_SIZE_BYTES) {
        setErrorMessage(t('tradeBooking.imageCapture.imageTooLarge'));
        setState('error');
        setTimeout(() => { setState('ready'); setErrorMessage(null); }, 4000);
        return;
      }

      // Convert to base64
      const base64 = await blobToBase64(imageBlob);

      // Store data URL for hover preview
      const dataUrl = `data:${mimeType};base64,${base64}`;
      setImageDataUrl(dataUrl);

      // Start extraction
      setState('processing');
      setErrorMessage(null);
      onExtractionStart();

      let multiTradeRedirected = false;
      try {
        const extractionResult = await tradeImageCaptureService.extractFromImage(
          clientId,
          base64,
          mimeType,
          imageBlob.size
        );

        // KAN-125: Check for multi-trade redirect
        const maybeRedirect = extractionResult as any;
        if (maybeRedirect.redirectToMulti && onMultiTradeDetected) {
          setState('completed');
          setResult(null); // No single-trade result
          multiTradeRedirected = true;
          // Don't call onExtractionEnd — the handler manages its own loading state
          onMultiTradeDetected(base64, mimeType, imageBlob.size, maybeRedirect.sourceTypeName || '');
        } else if (extractionResult.fieldsPopulated > 0) {
          setResult(extractionResult);
          setState('completed');
          onExtractionComplete(extractionResult);
        } else {
          setErrorMessage(t('tradeBooking.imageCapture.noDataFound'));
          setState('error');
          setImageDataUrl(null);
          setTimeout(() => { setState('ready'); setErrorMessage(null); }, 4000);
        }
      } catch (err: any) {
        setErrorMessage(t('tradeBooking.imageCapture.extractionError'));
        setState('error');
        setImageDataUrl(null);
        setTimeout(() => { setState('ready'); setErrorMessage(null); }, 4000);
      } finally {
        if (!multiTradeRedirected) onExtractionEnd();
      }
    } catch (err: any) {
      // Clipboard permission denied or API error
      setErrorMessage(t('tradeBooking.imageCapture.clipboardUnavailable'));
      setState('error');
      setTimeout(() => { setState('ready'); setErrorMessage(null); }, 4000);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setState('ready');
    setResult(null);
    setErrorMessage(null);
    setImageDataUrl(null);
    setShowPreview(false);
    onClear();
  };

  return (
    <>
      {errorMessage && state === 'error' && (
        <div className="capture-error-message">! {errorMessage}</div>
      )}
      <div
        ref={zoneRef}
        className={`trade-image-capture-zone ${state}${disabled ? ' disabled' : ''}`}
        onClick={handleClick}
        onMouseEnter={() => { if (state === 'completed' && imageDataUrl) setShowPreview(true); }}
        onMouseLeave={() => setShowPreview(false)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
      >
        {state === 'ready' && (
          <span>{t('tradeBooking.imageCapture.clickToPaste')}</span>
        )}

        {state === 'processing' && (
          <span>
            <span className="capture-spinner" />
            {t('tradeBooking.imageCapture.extracting')}
          </span>
        )}

        {state === 'completed' && result && (
          <div className="capture-completed-content">
            <span className="capture-summary">
              {result.sourceTypeName}
              {' \u00B7 '}
              {result.fieldsPopulated}
            </span>
            <button className="capture-clear-btn" onClick={handleClear}>
              {t('tradeBooking.imageCapture.clear')}
            </button>
          </div>
        )}

        {state === 'completed' && !result && (
          <div className="capture-completed-content">
            <span className="capture-summary">
              {t('tradeBooking.imageCapture.multiTradeDetected')}
            </span>
            <button className="capture-clear-btn" onClick={handleClear}>
              {t('tradeBooking.imageCapture.clear')}
            </button>
          </div>
        )}

        {state === 'error' && (
          <span>{t('tradeBooking.imageCapture.clickToPaste')}</span>
        )}

        {/* Hover preview of captured image */}
        {showPreview && imageDataUrl && (
          <div className="capture-preview-popover">
            <img src={imageDataUrl} alt="Captured screenshot" />
          </div>
        )}
      </div>
    </>
  );
};

/** Convert a Blob to a base64 string (without the data URL prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default TradeImageCapturePanel;
